import React, { useState, useEffect, useMemo } from 'react';
import {
  Workflow, Package, Plus, Play, Pause, Pencil, Search, Users,
  Mail, MessageCircle, PhoneCall, Clock, GitBranch, Bot, X,
  FlaskConical, AlertTriangle, ChevronDown, ChevronRight, Zap,
} from 'lucide-react';
import { toast } from 'react-toastify';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import { rrGetProductTree } from '@/actions/rr';
import {
  getProductJourneys,
  activateCampaign,
  pauseCampaign,
  getJourneyTriggers,
  DripCampaign,
  DripNodeType,
  JourneyTrigger,
} from '@/actions/drip-campaigns';
import CampaignBuilderModal from '../drip-campaigns/CampaignBuilderModal';
import AudienceModal from './AudienceModal';
import CreateJourneyModal from './CreateJourneyModal';
import { TreeCat, audienceSummary, coveredProductIdsForJourney } from './AudiencePicker';

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  paused: 'bg-amber-100 text-amber-700',
  archived: 'bg-red-100 text-red-700',
};

// Icon per node type — shows at a glance which channels a journey uses.
const NODE_ICON: Partial<Record<DripNodeType, React.ReactNode>> = {
  email: <Mail className="w-3.5 h-3.5" />,
  whatsapp: <MessageCircle className="w-3.5 h-3.5" />,
  agent_call: <PhoneCall className="w-3.5 h-3.5" />,
  auto_dial: <PhoneCall className="w-3.5 h-3.5" />,
  delay: <Clock className="w-3.5 h-3.5" />,
  condition: <GitBranch className="w-3.5 h-3.5" />,
  ai_call: <Bot className="w-3.5 h-3.5" />,
};

/**
 * Journeys.
 *
 * Listed by journey, not by product. A journey can cover one product, several, a whole
 * category, or everything — so a row per product cannot represent it. The old
 * product-first list showed a category-wide journey under a single product and marked the
 * rest "No journey yet", which was simply untrue.
 *
 * Product coverage is still answerable, in the panel at the bottom: it lists the products
 * no journey reaches, which is the actual question that view was good for.
 */
const ProductJourneysPage: React.FC = () => {
  const [tree, setTree] = useState<TreeCat[]>([]);
  const [journeys, setJourneys] = useState<DripCampaign[]>([]);
  // The full catalogue, including triggers the engine cannot fire yet. Kept whole so an
  // existing journey on a blocked trigger still shows its proper label instead of a raw
  // key; selection filters to `available` separately.
  const [triggers, setTriggers] = useState<JourneyTrigger[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [builderOpen, setBuilderOpen] = useState(false);
  const [activeJourney, setActiveJourney] = useState<DripCampaign | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [audienceFor, setAudienceFor] = useState<DripCampaign | null>(null);
  const [goLiveJourney, setGoLiveJourney] = useState<DripCampaign | null>(null);
  const [goingLive, setGoingLive] = useState(false);
  const [showGaps, setShowGaps] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [treeRes, jRes, trigRes] = await Promise.all([
        rrGetProductTree(),
        getProductJourneys(),
        getJourneyTriggers().catch(() => null),
      ]);
      setTree((treeRes?.data as TreeCat[] | null) || []);
      setJourneys((jRes?.data as DripCampaign[] | null) || []);
      if (trigRes?.success && Array.isArray(trigRes.data)) {
        setTriggers(trigRes.data);
      }
    } catch (e) {
      console.error('Error loading journeys', e);
      toast.error('Failed to load journeys');
    } finally {
      setLoading(false);
    }
  };

  const triggerLabel = (key?: string | null) =>
    triggers.find((t) => t.key === key)?.label || key || '—';

  // Only triggers the engine actually fires can be chosen. A journey built on an
  // unwired trigger would activate and then sit inert with nothing to explain why.
  const selectableTriggers = useMemo(
    () => triggers.filter((t) => t.available), [triggers]
  );

  // An existing journey on a blocked trigger will never start — worth flagging on its row
  // rather than leaving someone to wonder why nothing happens.
  const triggerInert = (key?: string | null) => {
    const t = triggers.find((x) => x.key === key);
    return !!t && !t.available;
  };

  // Products no journey reaches — resolved through each journey's real audience, not its
  // product_id_fk, so a category journey counts for everything it covers.
  const uncovered = useMemo(() => {
    const covered = new Set<number>();
    journeys.forEach((j) => coveredProductIdsForJourney(j, tree).forEach((id) => covered.add(id)));
    return tree
      .map((c) => ({ ...c, products: c.products.filter((p) => !covered.has(p.product_id)) }))
      .filter((c) => c.products.length > 0);
  }, [journeys, tree]);

  const stats = useMemo(() => {
    const allProducts = tree.flatMap((c) => c.products);
    const uncoveredCount = uncovered.reduce((n, c) => n + c.products.length, 0);
    return {
      journeys: journeys.length,
      active: journeys.filter((j) => j.status === 'active').length,
      covered: allProducts.length - uncoveredCount,
      products: allProducts.length,
    };
  }, [journeys, tree, uncovered]);

  const visibleJourneys = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return journeys;
    return journeys.filter((j) =>
      j.campaign_name.toLowerCase().includes(q) ||
      audienceSummary(j, tree).toLowerCase().includes(q) ||
      triggerLabel(j.anchor_event).toLowerCase().includes(q)
    );
  }, [journeys, search, tree, triggers]);

  const openBuilder = (journey: DripCampaign) => {
    setActiveJourney(journey);
    setBuilderOpen(true);
  };

  const toggleStatus = async (j: DripCampaign) => {
    try {
      if (j.status === 'active') {
        await pauseCampaign(j.id);
        toast.success('Journey paused');
      } else if (j.test_mode) {
        await activateCampaign(j.id);
        toast.success('Journey activated in TEST MODE — sends go to the test number only');
      } else {
        // Test mode is off, so activating starts messaging real customers. The API
        // refuses without confirm_live; ask before passing it rather than retrying.
        setGoLiveJourney(j);
        return;
      }
      loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to update journey');
    }
  };

  const confirmGoLive = async () => {
    if (!goLiveJourney) return;
    setGoingLive(true);
    try {
      await activateCampaign(goLiveJourney.id, true);
      toast.success('Journey is LIVE — real customers will now be messaged');
      setGoLiveJourney(null);
      loadAll();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to activate journey');
    } finally {
      setGoingLive(false);
    }
  };

  const channelChips = (j: DripCampaign) => {
    const types = Array.from(new Set((j.steps || []).map((s) => s.node_type).filter(Boolean)));
    if (!types.length) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="w-3 h-3" /> No steps yet
        </span>
      );
    }
    return (
      <div className="flex items-center gap-1.5">
        {types.map((t) => (
          <span key={t}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[11px]">
            {NODE_ICON[t as DripNodeType]}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="Journeys"
        description="Multi-channel post-purchase journeys — WhatsApp and agent calls, timed to the customer lifecycle. Each journey chooses the products it applies to."
        icon={<Workflow className="w-6 h-6" />}
      />

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Journeys', value: stats.journeys },
            { label: 'Active', value: stats.active },
            { label: 'Products covered', value: `${stats.covered} / ${stats.products}` },
          ].map((s) => (
            <div key={s.label}
                 className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{s.label}</div>
              <div className="text-2xl font-semibold text-gray-900 dark:text-white tabular-nums">
                {s.value}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 flex-1 max-w-md">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search journeys, audience, trigger…"
              className="flex-1 text-sm text-gray-900 dark:text-white border-0 outline-none bg-transparent"
            />
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-brandColor text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4" /> Create journey
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor" />
            <p className="mt-4 text-gray-500">Loading journeys…</p>
          </div>
        ) : visibleJourneys.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <Workflow className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              {search ? `No journeys match “${search}”.` : 'No journeys yet.'}
            </p>
            {!search && (
              <button
                onClick={() => setCreateOpen(true)}
                className="mt-4 px-4 py-2 text-sm rounded-lg bg-brandColor text-white hover:opacity-90"
              >
                Create the first one
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
            {visibleJourneys.map((j) => (
              <div key={j.id} className="flex flex-wrap items-center gap-4 px-4 py-3">
                <div className="flex-1 min-w-[14rem]">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {j.campaign_name}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                    <span
                      className={`inline-flex items-center gap-1 text-xs ${
                        triggerInert(j.anchor_event)
                          ? 'text-red-600 dark:text-red-400 font-medium'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                      title={triggerInert(j.anchor_event)
                        ? 'This trigger is not wired up yet — this journey will never start on its own'
                        : undefined}
                    >
                      <Zap className="w-3 h-3" /> {triggerLabel(j.anchor_event)}
                      {triggerInert(j.anchor_event) && ' — never fires'}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                      <Users className="w-3 h-3" /> {audienceSummary(j, tree)}
                    </span>
                    {channelChips(j)}
                  </div>
                </div>

                {/* Test mode is invisible state otherwise — an active test-mode journey
                    looks identical to a live one. */}
                {j.test_mode && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                    title={j.test_recipient_phone
                      ? `Sends go to ${j.test_recipient_phone} only`
                      : 'Sends go to the configured test number only'}
                  >
                    <FlaskConical className="w-3 h-3" /> Test mode
                  </span>
                )}

                <span className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${STATUS_BADGE[j.status] || STATUS_BADGE.draft}`}>
                  {j.status}
                </span>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => toggleStatus(j)}
                    className={`flex items-center gap-1 px-2.5 py-1.5 text-xs rounded text-white ${
                      j.status === 'active'
                        ? 'bg-amber-500 hover:bg-amber-600'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                    title={j.status === 'active' ? 'Pause' : 'Activate'}
                  >
                    {j.status === 'active'
                      ? <Pause className="w-3.5 h-3.5" />
                      : <Play className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={() => setAudienceFor(j)}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    title="Choose which products this journey covers"
                  >
                    <Users className="w-3.5 h-3.5" /> Audience
                  </button>
                  <button
                    onClick={() => openBuilder(j)}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded bg-brandColor text-white hover:opacity-90"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit flow
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Coverage. The one thing the product-first list was genuinely good for, kept as
            a panel rather than 76 rows of mostly "no journey yet". */}
        {!loading && tree.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowGaps((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-3 text-left"
            >
              {showGaps
                ? <ChevronDown className="w-4 h-4 text-gray-400" />
                : <ChevronRight className="w-4 h-4 text-gray-400" />}
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {stats.products - stats.covered === 0
                  ? 'Every product is covered by a journey'
                  : `${stats.products - stats.covered} products have no journey`}
              </span>
            </button>
            {showGaps && (
              <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-700 pt-3">
                {uncovered.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Nothing uncovered.
                  </p>
                ) : uncovered.map((cat) => (
                  <div key={cat.cat_id}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-2 h-2 rounded-full"
                            style={{ background: cat.colour || '#9ca3af' }} />
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                        {cat.cat}
                      </span>
                      <span className="text-xs text-gray-400">({cat.products.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {cat.products.map((p) => (
                        <span key={p.product_id}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                          <Package className="w-3 h-3" /> {p.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {createOpen && (
        <CreateJourneyModal
          tree={tree}
          triggers={selectableTriggers}
          onClose={() => setCreateOpen(false)}
          onCreated={(j) => {
            setCreateOpen(false);
            loadAll();
            openBuilder(j);
          }}
        />
      )}

      {audienceFor && (
        <AudienceModal
          campaign={audienceFor}
          tree={tree}
          onClose={() => setAudienceFor(null)}
          onSaved={loadAll}
        />
      )}

      {/* Going live is the one irreversible step here — a sent WhatsApp cannot be
          recalled. */}
      {goLiveJourney && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-start gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900 flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-300" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Activate for real customers?
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                  {goLiveJourney.campaign_name}
                </p>
              </div>
              <button onClick={() => setGoLiveJourney(null)}
                      className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Test mode is <strong>off</strong> for this journey. Activating it will send
                real WhatsApp messages and raise real agent call tasks for every customer
                who matches the trigger.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Sent messages cannot be recalled. If you have not walked this flow through
                in test mode yet, turn test mode on first.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
              <button onClick={() => setGoLiveJourney(null)}
                      className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                Cancel
              </button>
              <button onClick={confirmGoLive} disabled={goingLive}
                      className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                {goingLive ? 'Activating…' : 'Yes, go live'}
              </button>
            </div>
          </div>
        </div>
      )}

      {builderOpen && (
        <CampaignBuilderModal
          isOpen={builderOpen}
          onClose={() => { setBuilderOpen(false); setActiveJourney(null); }}
          campaign={activeJourney}
          onSuccess={loadAll}
        />
      )}
    </div>
  );
};

export default ProductJourneysPage;
